import { useState } from 'react'
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'

/**
 * Refresh every query a new stash document can change. With an `assessmentId`
 * only that assessment refetches; without one (the server picked the vendor's
 * open assessment for us) every cached assessment is invalidated rather than
 * leaving a stale intake checklist behind.
 */
export function invalidateVendorDocumentQueries(
  queryClient: QueryClient,
  vendorId: string,
  assessmentId?: string
) {
  void queryClient.invalidateQueries({
    queryKey: trpc.vendorManagement.listDocuments.queryKey({ vendorId }),
  })
  void queryClient.invalidateQueries({
    queryKey: assessmentId
      ? trpc.vendorAssessment.getById.queryKey({ id: assessmentId })
      : trpc.vendorAssessment.getById.queryKey(),
  })
  void queryClient.invalidateQueries({
    queryKey: assessmentId
      ? trpc.vendorAssessment.listInputs.queryKey({ assessmentId })
      : trpc.vendorAssessment.listInputs.queryKey(),
  })
}

/** Mirrors the server-side cap in vendorManagement.uploadDocument. */
const MAX_SIZE = 50 * 1024 * 1024

function readAsBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve((reader.result as string).split(',')[1] || '')
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Upload documents to a vendor's stash from anywhere in the assessment UI.
 *
 * Passing `assessmentId` + `documentType` lets the server flip the matching
 * evidence request to RECEIVED, which is what makes the intake checklist move —
 * so the two upload surfaces inside an assessment share this one hook rather
 * than re-deriving the base64 + invalidation dance.
 */
export function useVendorDocumentUpload(vendorId: string) {
  const queryClient = useQueryClient()
  const uploadMutation = useMutation(
    trpc.vendorManagement.uploadDocument.mutationOptions()
  )
  const [uploading, setUploading] = useState(false)

  const upload = async (
    files: FileList | File[],
    options?: { documentType?: string; assessmentId?: string }
  ): Promise<{ uploaded: number; evidenceRequestsSatisfied: number }> => {
    const list = Array.from(files)
    const tooBig = list.filter((f) => f.size > MAX_SIZE)
    const ok = list.filter((f) => f.size <= MAX_SIZE)
    if (tooBig.length > 0) {
      toast.error(`${tooBig.length} file(s) exceed the 50 MB limit and were skipped`)
    }
    if (ok.length === 0) return { uploaded: 0, evidenceRequestsSatisfied: 0 }

    setUploading(true)
    try {
      const results = await Promise.allSettled(
        ok.map(async (file) => {
          const base64Data = await readAsBase64(file)
          return (uploadMutation as any).mutateAsync({
            vendorId,
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            base64Data,
            size: file.size,
            ...(options?.documentType ? { documentType: options.documentType } : {}),
            ...(options?.assessmentId ? { assessmentId: options.assessmentId } : {}),
          })
        })
      )

      const fulfilled = results.filter(
        (r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled'
      )
      if (fulfilled.length < ok.length) {
        toast.warning(`Uploaded ${fulfilled.length}/${ok.length} documents`)
      }

      const evidenceRequestsSatisfied = fulfilled.reduce(
        (sum, r) => sum + (r.value?.evidenceRequestsSatisfied ?? 0),
        0
      )

      invalidateVendorDocumentQueries(queryClient, vendorId, options?.assessmentId)
      return { uploaded: fulfilled.length, evidenceRequestsSatisfied }
    } finally {
      setUploading(false)
    }
  }

  return { upload, uploading }
}
